import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Stack,
  Text,
  Group,
  Paper,
  AppShell,
  ScrollArea,
  Box,
  Loader,
  Transition,
  Affix,
  ActionIcon,
  Indicator,
  Badge,
  Tooltip,
  TextInput,
  Textarea,
  Modal,
  Button,
  NumberInput,
  Popover,
} from '@mantine/core';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { CommentsQuery, SortOrder } from './__generated__/graphql';
import { gql } from './__generated__';
import { useDisclosure, useIntersection, useLocalStorage } from '@mantine/hooks';
import {
  IconArrowUp,
  IconConfetti,
  IconCopy,
  IconMessageUp,
  IconSettings,
  IconX,
} from '@tabler/icons-react';
//import { useAutoAnimate } from '@formkit/auto-animate/react';
import { showNotification } from '@mantine/notifications';
import { hasActionIntent } from './utils/commentIntent';

const replyCommentToDocument = gql(/* GraphQL */ `
  mutation ReplyCommentTo($objectId: String!, $message: String!) {
    replyCommentTo(objectId: $objectId, message: $message)
  }
`);

const commentsDocument = gql(/* GraphQL */ `
  query Comments(
    $where: CommentWhereInput
    $orderBy: [CommentOrderByWithRelationInput!]
    $skip: Int
    $take: Int
    $cursor: CommentWhereUniqueInput
  ) {
    comments(where: $where, orderBy: $orderBy, skip: $skip, take: $take, cursor: $cursor) {
      id
      commentId
      body
      createdAt
      customer {
        id
        name
        avatar
        active
        reason
        orders(where: { status: { equals: CREATED } }) {
          id
          package
          total
        }
      }
      parent {
        id
        body
        createdAt
        customer {
          id
          name
          avatar
        }
      }
    }
  }
`);

const commentCreatedDocument = gql(/* GraphQL */ `
  subscription CommentCreated {
    commentCreated {
      id
      commentId
      body
      createdAt
      customer {
        id
        name
        avatar
        active
        reason
        orders(where: { status: { equals: CREATED } }) {
          id
          package
          total
        }
      }
      parent {
        id
        body
        createdAt
        customer {
          id
          name
          avatar
        }
      }
    }
  }
`);

const INITIAL_SIZE = 10;
const FETCH_SIZE = 5;
const MAX_COMMENTS = 500;
const MAX_UNREAD_IDS = 30;
// A solved mark is only ever visible while its comment is still in the feed,
// and the feed holds at most MAX_COMMENTS — anything older can never render,
// so the cap keeps localStorage bounded (~28 B per cuid, ~14 kB full) instead
// of growing across lives.
const MAX_SOLVED_IDS = MAX_COMMENTS;

const GIVEAWAY_DEFAULTS = {
  durationSeconds: 5,
  poolSize: 100,
  startGapMs: 50,
  growth: 1.085,
};

const GIVEAWAY_LIMITS = {
  durationMin: 2,
  durationMax: 20,
  poolMin: 20,
  poolMax: 500,
};

const giveawayKeyframes = `
@keyframes giveawaySpinPulse {
  0%   { transform: scale(0.97); filter: brightness(0.95); }
  50%  { transform: scale(1.06); filter: brightness(1.1); }
  100% { transform: scale(0.97); filter: brightness(0.95); }
}
@keyframes giveawayPop {
  0%   { transform: scale(0.4); opacity: 0; }
  60%  { transform: scale(1.18); opacity: 1; }
  80%  { transform: scale(0.96); }
  100% { transform: scale(1); opacity: 1; }
}
@keyframes giveawayDot {
  0%, 80%, 100% { opacity: 0.2; transform: translateY(0); }
  40%           { opacity: 1;   transform: translateY(-4px); }
}
@keyframes giveawayShimmer {
  0%   { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}
@keyframes giveawayWheelTick {
  0%   { transform: translateY(28%); opacity: 0.55; }
  100% { transform: translateY(0); opacity: 1; }
}
@keyframes giveawayLockIn {
  0%   { transform: scale(1);    color: var(--mantine-color-grape-3); }
  35%  { transform: scale(1.32); color: var(--mantine-color-yellow-3); }
  70%  { transform: scale(1.05); color: var(--mantine-color-yellow-4); }
  100% { transform: scale(1.12); color: var(--mantine-color-yellow-4); }
}
@keyframes commentArrive {
  0%   { transform: translateX(-12px); opacity: 0; }
  100% { transform: translateX(0); opacity: 1; }
}
`;

export const CommentList = ({ opened }: { opened: boolean }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const [winnerModalOpened, winnerModal] = useDisclosure(false);

  const [showScrollTop, setShowScrollTop] = useState(false);
  const [unreadIds, setUnreadIds] = useState<Set<string>>(() => new Set());
  const showScrollTopRef = useRef(showScrollTop);
  useEffect(() => {
    showScrollTopRef.current = showScrollTop;
  }, [showScrollTop]);

  const scrollToTop = () => {
    containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    setUnreadIds(new Set());
  };

  const [replyTo, setReplyTo] = useState<CommentsQuery['comments'][number] | null>(null);

  // Comments needing action are cleared by opening the customer's order, which
  // is the operator's "I handled this" gesture. Browser-local on purpose.
  const [solvedIds, setSolvedIds] = useLocalStorage<string[]>({
    key: 'comments-solved-ids',
    defaultValue: [],
  });

  const solvedSet = useMemo(() => new Set(solvedIds), [solvedIds]);

  const markSolved = (id: string) =>
    setSolvedIds((prev) => (prev.includes(id) ? prev : [...prev, id].slice(-MAX_SOLVED_IDS)));

  // const [animationRef] = useAutoAnimate({ duration: 750 });
  const inputRef = useRef<HTMLInputElement>(null);

  const { ref, entry } = useIntersection({
    root: containerRef.current,
    threshold: 0.9,
  });

  const { data, refetch, fetchMore, loading, subscribeToMore } = useQuery(commentsDocument, {
    variables: {
      take: INITIAL_SIZE,
      orderBy: { createdAt: SortOrder.Desc },
    },
    notifyOnNetworkStatusChange: true,
    skip: !opened,
  });

  const [replyCommentTo, replyCommentToMutation] = useMutation(replyCommentToDocument);

  const fetchedCountRef = useRef(INITIAL_SIZE);

  const hasMore =
    fetchedCountRef.current >= INITIAL_SIZE && fetchedCountRef.current % FETCH_SIZE === 0;

  const fetchingMoreRef = useRef(false);
  const unsubscribeRef = useRef<() => void>();

  const scheduleUnreadAutoClear = (ids: string[], delay = 3000) => {
    setTimeout(() => {
      setUnreadIds((prev) => {
        let next: Set<string> | null = null;
        for (const id of ids) {
          if (prev.has(id)) {
            if (!next) next = new Set(prev);
            next.delete(id);
          }
        }
        return next ?? prev;
      });
    }, delay);
  };

  const [winners, setWinners] = useState<CommentsQuery['comments'][number][]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isLocking, setIsLocking] = useState(false);
  const [poolSize, setPoolSize] = useState<number | null>(null);
  type WheelEntry = { name: string; body: string } | null;
  const emptyWheel: [WheelEntry, WheelEntry, WheelEntry] = [null, null, null];
  const [wheelStrip, setWheelStrip] = useState<[WheelEntry, WheelEntry, WheelEntry]>(emptyWheel);
  const drawTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [drawDuration, setDrawDuration] = useLocalStorage<number>({
    key: 'giveaway-duration-seconds-v2',
    defaultValue: GIVEAWAY_DEFAULTS.durationSeconds,
  });
  const [drawPoolSize, setDrawPoolSize] = useLocalStorage<number>({
    key: 'giveaway-pool-size',
    defaultValue: GIVEAWAY_DEFAULTS.poolSize,
  });

  const winner = winners.length > 0 ? winners[winners.length - 1] : null;

  const winnersExportText = winners.map((w, i) => `${i + 1}. ${w.customer.name}`).join('\n');

  const copyWinners = async () => {
    try {
      await navigator.clipboard.writeText(winnersExportText);
      showNotification({ message: 'Skopírované do schránky', color: 'green' });
    } catch {
      showNotification({ message: 'Nepodarilo sa skopírovať', color: 'red' });
    }
  };

  const [fetchDrawPool] = useLazyQuery(commentsDocument, {
    fetchPolicy: 'network-only',
    variables: {
      take: drawPoolSize,
      orderBy: { createdAt: SortOrder.Desc },
    },
  });

  const startDraw = async () => {
    if (isDrawing) return;

    setIsDrawing(true);

    const result = await fetchDrawPool({
      variables: {
        where: { customer: { is: { active: { equals: true } } } },
        orderBy: { createdAt: SortOrder.Desc },
        take: drawPoolSize,
      },
    });

    const fetched = result.data?.comments ?? [];

    const winnerIds = new Set(winners.map((w) => w.customer.id));
    const seen = new Set<string>();
    const pool: CommentsQuery['comments'] = [];
    for (const c of fetched) {
      if (winnerIds.has(c.customer.id)) continue;
      if (seen.has(c.customer.id)) continue;
      seen.add(c.customer.id);
      pool.push(c);
    }

    setPoolSize(pool.length);

    if (pool.length === 0) {
      setIsDrawing(false);
      showNotification({
        title: 'Nemožno losovať',
        message: 'Pool je prázdny — žiadni dostupní zákazníci',
        color: 'yellow',
      });
      return;
    }

    if (drawTimeoutRef.current) clearTimeout(drawTimeoutRef.current);

    const TOTAL_MS = drawDuration * 1000;
    const START_GAP = GIVEAWAY_DEFAULTS.startGapMs;
    const GROWTH = GIVEAWAY_DEFAULTS.growth;
    const LOCK_HOLD_MS = 1800;

    const picked = pool[Math.floor(Math.random() * pool.length)];
    const pickedEntry: WheelEntry = {
      name: picked.customer.name,
      body: picked.body,
    };

    const randomEntry = (): WheelEntry => {
      const c = pool[Math.floor(Math.random() * pool.length)];
      return { name: c.customer.name, body: c.body };
    };
    setWheelStrip([randomEntry(), randomEntry(), randomEntry()]);

    let elapsed = 0;
    let gap = START_GAP;
    const tick = () => {
      const elapsedAfter = elapsed + gap;
      const nextGap = gap * GROWTH;
      const isLandingTick = elapsedAfter >= TOTAL_MS;
      const nextWillBeLanding = !isLandingTick && elapsedAfter + nextGap >= TOTAL_MS;

      if (isLandingTick) {
        // Final rotation — winner was pre-placed in the bottom slot last tick,
        // so it visibly slides up into the middle here.
        setWheelStrip(([, c, n]) => [c, n, null]);
        setIsLocking(true);
        drawTimeoutRef.current = setTimeout(() => {
          setIsLocking(false);
          setWinners((prevWinners) => [...prevWinners, picked]);
          setIsDrawing(false);
        }, LOCK_HOLD_MS);
        return;
      }

      const newBottom: WheelEntry = nextWillBeLanding ? pickedEntry : randomEntry();
      setWheelStrip(([, c, n]) => [c, n, newBottom]);
      elapsed = elapsedAfter;
      gap = nextGap;
      drawTimeoutRef.current = setTimeout(tick, gap);
    };
    drawTimeoutRef.current = setTimeout(tick, gap);
  };

  useEffect(() => {
    if (
      !opened ||
      !entry?.isIntersecting ||
      loading ||
      !hasMore ||
      !data?.comments.length ||
      fetchingMoreRef.current
    )
      return;

    const lastComment = data.comments.at(-1);
    if (!lastComment) return;

    fetchingMoreRef.current = true;

    fetchMore({
      variables: {
        take: FETCH_SIZE,
        skip: 1,
        cursor: { id: lastComment.id },
        orderBy: { createdAt: SortOrder.Desc },
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        fetchingMoreRef.current = false;

        if (!fetchMoreResult?.comments?.length) return prev;

        fetchedCountRef.current += fetchMoreResult.comments.length;

        return {
          comments: [...prev.comments, ...fetchMoreResult.comments].slice(0, MAX_COMMENTS),
        };
      },
    }).finally(() => {
      fetchingMoreRef.current = false;
    });
  }, [data?.comments, entry?.isIntersecting, fetchMore, hasMore, loading, opened]);

  useEffect(() => {
    if (!opened) setUnreadIds(new Set());
  }, [opened]);

  useEffect(() => {
    if (!opened || winnerModalOpened) return;

    refetch();

    unsubscribeRef.current = subscribeToMore({
      document: commentCreatedDocument,
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) return prev;

        const newComment = subscriptionData.data.commentCreated;

        if (prev.comments.some((c) => c.id === newComment.id)) return prev;

        setUnreadIds((prevIds) => {
          const next = new Set(prevIds);
          next.add(newComment.id);
          while (next.size > MAX_UNREAD_IDS) {
            const oldest = next.values().next().value;
            if (oldest === undefined) break;
            next.delete(oldest);
          }
          return next;
        });
        if (!showScrollTopRef.current) {
          scheduleUnreadAutoClear([newComment.id]);
        }

        return {
          comments: [newComment, ...prev.comments].slice(0, MAX_COMMENTS),
        };
      },
    });

    return () => {
      unsubscribeRef.current?.();
    };
  }, [subscribeToMore, opened, refetch, winnerModalOpened]);

  useEffect(() => {
    return () => {
      if (drawTimeoutRef.current) clearTimeout(drawTimeoutRef.current);
    };
  }, []);

  return (
    <>
      <AppShell.Section
        grow
        component={ScrollArea}
        viewportRef={containerRef}
        offsetScrollbars
        onScrollPositionChange={({ y }) => {
          setShowScrollTop(y > 200);
          if (y < 50) {
            setUnreadIds((prev) => (prev.size === 0 ? prev : new Set()));
          }
        }}
        type="never"
        viewportProps={{
          style: {
            overscrollBehavior: 'contain',
            paddingBottom: 'calc(var(--mantine-spacing-sm) * 3)',
          },
        }}
      >
        <Stack gap={6}>
          {data?.comments.map((comment) => {
            const actionIntent = hasActionIntent(comment.body);
            const linkedOrder = comment.customer.orders?.[0];
            const solved = actionIntent && solvedSet.has(comment.id);

            return (
              <Group
                key={comment.id}
                align="center"
                gap="sm"
                wrap="nowrap"
                p={2}
                style={{
                  borderRadius: 'var(--mantine-radius-md)',
                  ...(unreadIds.has(comment.id) && {
                    animation: 'commentArrive 450ms cubic-bezier(0.22, 1, 0.36, 1)',
                  }),
                }}
              >
                <Indicator
                  size={10}
                  color="yellow"
                  processing
                  disabled={!unreadIds.has(comment.id)}
                >
                  {linkedOrder ? (
                    <Tooltip label={`Otvoriť objednávku #${linkedOrder.package}`}>
                      <Avatar
                        component="a"
                        href={`/order/${linkedOrder.id}`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => {
                          if (actionIntent) markSolved(comment.id);
                        }}
                        src={comment.customer.avatar}
                        name={comment.customer.name}
                        color="initials"
                        size="md"
                        radius="sm"
                        style={{ cursor: 'pointer' }}
                      />
                    </Tooltip>
                  ) : (
                    <Avatar
                      src={comment.customer.avatar}
                      name={comment.customer.name}
                      color="initials"
                      size="md"
                      radius="sm"
                    />
                  )}
                </Indicator>
                <Stack gap={0} style={{ flex: 1 }}>
                  {comment.parent && (
                    <Paper
                      px="sm"
                      py={6}
                      radius="md"
                      withBorder
                      bg="dark.7"
                      style={{ borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}
                    >
                      <Group gap={0} justify="space-between" align="start" mb={2}>
                        <Group gap={4}>
                          <Text size="xs" c="dimmed">
                            ↩ Odpoveď pre
                          </Text>
                          <Avatar
                            src={comment.parent.customer.avatar}
                            name={comment.parent.customer.name}
                            color="initials"
                            size={16}
                          />
                          <Text size="xs" fw={600}>
                            {comment.parent.customer.name}
                          </Text>
                        </Group>
                        <Text size="xs" c="dimmed">
                          {dayjs(comment.parent.createdAt).format('HH:mm:ss')}
                        </Text>
                      </Group>
                      <Text
                        size="xs"
                        lineClamp={2}
                        style={{
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {comment.parent.body}
                      </Text>
                    </Paper>
                  )}
                  <Paper
                    px="sm"
                    py={6}
                    radius="md"
                    withBorder
                    bg={
                      solved
                        ? 'rgba(64, 192, 87, 0.1)'
                        : actionIntent
                          ? 'rgba(250, 176, 5, 0.12)'
                          : (comment.customer.orders?.length ?? 0) > 0
                            ? 'dark.5'
                            : 'dark.6'
                    }
                    style={{
                      borderColor:
                        replyTo?.id === comment.id
                          ? 'var(--mantine-color-blue-7)'
                          : solved
                            ? 'var(--mantine-color-green-9)'
                            : actionIntent
                              ? 'var(--mantine-color-yellow-7)'
                              : undefined,
                      borderTopLeftRadius: comment.parent ? 0 : undefined,
                      borderTopRightRadius: comment.parent ? 0 : undefined,
                      cursor: 'pointer',
                    }}
                    onClick={() => setReplyTo(replyTo?.id !== comment.id ? comment : null)}
                  >
                    <Group justify="space-between" align="start" wrap="nowrap">
                      <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                        <Group gap="xs" wrap="nowrap">
                          <Text fw={600} size="sm" truncate="end">
                            {comment.customer.name}
                          </Text>
                          {!comment.customer.active && (
                            <Tooltip label={comment.customer.reason}>
                              <Badge size="xs" color="red">
                                X
                              </Badge>
                            </Tooltip>
                          )}
                        </Group>
                        <Text
                          size="sm"
                          lineClamp={4}
                          style={{
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          {comment.body}
                        </Text>
                      </Stack>
                      <Stack align="end" gap={2} style={{ whiteSpace: 'nowrap' }}>
                        <Text size="xs" c="dimmed">
                          {dayjs(comment.createdAt).format('HH:mm:ss')}
                        </Text>
                        {comment.customer.orders?.map((order) => (
                          <Stack key={order.id} gap={2} align="end">
                            <Text size="xs" c="dimmed">
                              #{order.package}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {order.total} €
                            </Text>
                          </Stack>
                        ))}
                      </Stack>
                    </Group>
                  </Paper>
                </Stack>
              </Group>
            );
          })}
          <Box ref={ref} h="sm" />
        </Stack>
        {loading && (
          <Group justify="center" my="sm">
            <Loader size="sm" type="dots" />
          </Group>
        )}

        {!hasMore && (
          <Group justify="center" my="sm">
            <Text size="sm" c="dimmed">
              No more comments to show
            </Text>
          </Group>
        )}
        <Affix position={{ bottom: replyTo ? 128 : 32, right: 16 }}>
          <Transition transition="pop" mounted={showScrollTop} duration={200} timingFunction="ease">
            {(styles) => (
              <Indicator
                style={styles}
                size={18}
                offset={2}
                color="yellow"
                disabled={unreadIds.size === 0}
                label={unreadIds.size > 99 ? '99+' : unreadIds.size}
              >
                <ActionIcon variant="filled" size="md" onClick={scrollToTop}>
                  {unreadIds.size > 0 ? <IconMessageUp size={16} /> : <IconArrowUp size={16} />}
                </ActionIcon>
              </Indicator>
            )}
          </Transition>
        </Affix>

        <Affix position={{ bottom: replyTo ? 160 : 64, right: 16 }}>
          <Transition transition="pop" mounted={opened} duration={200} timingFunction="ease">
            {(styles) => (
              <ActionIcon
                style={styles}
                size="md"
                variant="filled"
                color="grape"
                onClick={winnerModal.open}
              >
                <IconConfetti size={16} />
              </ActionIcon>
            )}
          </Transition>
        </Affix>
        <Box
          pos="absolute"
          bottom={0}
          left={0}
          right={0}
          h={24}
          style={{
            background: 'linear-gradient(to top, var(--mantine-color-dark-7), transparent)',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
      </AppShell.Section>
      <Transition transition="slide-up" mounted={!!replyTo} timingFunction="ease">
        {(styles) => (
          <AppShell.Section
            pt={2}
            style={{
              ...styles,
              background: 'linear-gradient(to bottom, transparent, var(--mantine-color-dark-7))',
              zIndex: 1,
            }}
          >
            <Group justify="space-between" align="center" wrap="nowrap" mb={4}>
              <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                <Text size="sm" c="gray" truncate="end">
                  ↩ Replying to {replyTo?.customer.name}
                </Text>
                <Text
                  size="sm"
                  c="dimmed"
                  lineClamp={2}
                  style={{
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {replyTo?.body}
                </Text>
              </Stack>
              <ActionIcon
                variant="light"
                size="md"
                onClick={() => setReplyTo(null)}
                color="red"
                opacity={0.7}
              >
                <IconX style={{ width: '70%', height: '70%' }} stroke={1.5} />
              </ActionIcon>
            </Group>

            <TextInput
              ref={inputRef}
              placeholder="Write a comment..."
              onKeyDown={async (e) => {
                if (e.key !== 'Enter' || !inputRef.current || !replyTo) return;

                e.preventDefault();

                const message = inputRef.current.value.trim();

                try {
                  await replyCommentTo({
                    variables: {
                      objectId: replyTo.commentId,
                      message,
                    },
                  });

                  inputRef.current.value = '';
                  setReplyTo(null);

                  showNotification({
                    title: `Replied to ${replyTo.customer.name}`,
                    message,
                    color: 'green',
                  });
                } catch (error) {
                  showNotification({
                    title: 'Failed to send reply',
                    message: error instanceof Error ? error.message : 'Unknown error',
                    color: 'red',
                  });
                }
              }}
              rightSection={replyCommentToMutation.loading && <Loader size={16} />}
              disabled={replyCommentToMutation.loading}
            />
          </AppShell.Section>
        )}
      </Transition>
      <Modal
        opened={winnerModalOpened}
        onClose={() => {
          winnerModal.close();
          setWinners([]);
          setIsDrawing(false);
          setIsLocking(false);
          setPoolSize(null);
          setWheelStrip([null, null, null]);
          if (drawTimeoutRef.current) {
            clearTimeout(drawTimeoutRef.current);
            drawTimeoutRef.current = null;
          }
        }}
        title={
          <Group gap="xs" align="center">
            <Text fw={700}>Žrebovanie výhercov</Text>
            <Popover position="bottom-start" withArrow shadow="md" trapFocus>
              <Popover.Target>
                <Tooltip label="Nastavenia losovania">
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="sm"
                    aria-label="Nastavenia losovania"
                  >
                    <IconSettings size={16} />
                  </ActionIcon>
                </Tooltip>
              </Popover.Target>
              <Popover.Dropdown>
                <Stack gap="xs" miw={260}>
                  <Text size="xs" c="dimmed">
                    Zo zoznamu posledných {drawPoolSize} komentárov
                    {poolSize !== null && ` · ${poolSize} unikátnych zákazníkov`}
                  </Text>
                  <NumberInput
                    label="Trvanie losovania (s)"
                    value={drawDuration}
                    onChange={(v) => {
                      const n = typeof v === 'number' ? v : Number(v);
                      if (Number.isFinite(n)) {
                        setDrawDuration(
                          Math.min(
                            GIVEAWAY_LIMITS.durationMax,
                            Math.max(GIVEAWAY_LIMITS.durationMin, n)
                          )
                        );
                      }
                    }}
                    min={GIVEAWAY_LIMITS.durationMin}
                    max={GIVEAWAY_LIMITS.durationMax}
                    step={1}
                    clampBehavior="strict"
                  />
                  <NumberInput
                    label="Veľkosť poolu (počet komentárov)"
                    value={drawPoolSize}
                    onChange={(v) => {
                      const n = typeof v === 'number' ? v : Number(v);
                      if (Number.isFinite(n)) {
                        setDrawPoolSize(
                          Math.min(GIVEAWAY_LIMITS.poolMax, Math.max(GIVEAWAY_LIMITS.poolMin, n))
                        );
                      }
                    }}
                    min={GIVEAWAY_LIMITS.poolMin}
                    max={GIVEAWAY_LIMITS.poolMax}
                    step={10}
                    clampBehavior="strict"
                  />
                  <Button
                    variant="subtle"
                    size="xs"
                    onClick={() => {
                      setDrawDuration(GIVEAWAY_DEFAULTS.durationSeconds);
                      setDrawPoolSize(GIVEAWAY_DEFAULTS.poolSize);
                    }}
                  >
                    Obnoviť predvolené
                  </Button>
                </Stack>
              </Popover.Dropdown>
            </Popover>
          </Group>
        }
        centered
        size="xl"
      >
        <style>{giveawayKeyframes}</style>
        <Stack
          align="center"
          justify="center"
          mih={360}
          bg={winner && !isDrawing ? 'green.7' : undefined}
          c={winner && !isDrawing ? 'dark.9' : undefined}
          p="md"
          style={{
            borderRadius: 'var(--mantine-radius-md)',
            transition: 'background-color 500ms ease',
            overflow: 'hidden',
          }}
        >
          {winner && !isDrawing && !isLocking ? (
            <Stack
              align="center"
              gap="xs"
              key={winner.id}
              style={{ animation: 'giveawayPop 700ms cubic-bezier(0.22, 1, 0.36, 1) both' }}
            >
              <Group gap="lg" wrap="nowrap" align="center">
                <IconConfetti size={56} color="var(--mantine-color-yellow-4)" />
                <Avatar
                  size={120}
                  src={winner.customer.avatar}
                  name={winner.customer.name}
                  color="initials"
                />
                <IconConfetti
                  size={56}
                  color="var(--mantine-color-yellow-4)"
                  style={{ transform: 'scaleX(-1)' }}
                />
              </Group>
              <Text fz={88} fw={900} ta="center" lh={1}>
                {winner.customer.name}
              </Text>
              <Text
                fz={88}
                fw={900}
                ta="center"
                lh={1}
                style={{ transform: 'scaleX(-1)', opacity: 0.85 }}
              >
                {winner.customer.name}
              </Text>
              <Text
                size="md"
                ta="center"
                lineClamp={3}
                maw={640}
                style={{
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {winner.body}
              </Text>
              <Text size="sm" fw={500}>
                {dayjs(winner.createdAt).format('DD.MM.YYYY HH:mm:ss')}
              </Text>
            </Stack>
          ) : isDrawing || isLocking ? (
            <Stack align="center" justify="center" gap="sm" mih={260} w="100%">
              <Box
                w="100%"
                maw={640}
                style={{
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: 'var(--mantine-radius-md)',
                  background: isLocking
                    ? 'linear-gradient(180deg, rgba(250,176,5,0.1) 0%, rgba(250,176,5,0.22) 50%, rgba(250,176,5,0.1) 100%)'
                    : 'linear-gradient(180deg, rgba(190,75,219,0.08) 0%, rgba(190,75,219,0.18) 50%, rgba(190,75,219,0.08) 100%)',
                  border: isLocking
                    ? '1px solid var(--mantine-color-yellow-5)'
                    : '1px solid var(--mantine-color-grape-7)',
                  transition: 'background 200ms ease, border-color 200ms ease',
                }}
              >
                <Stack
                  gap={4}
                  align="center"
                  py="md"
                  px="sm"
                  key={`${wheelStrip[1]?.name ?? ''}|${wheelStrip[2]?.name ?? ''}`}
                  style={isLocking ? undefined : { animation: 'giveawayWheelTick 180ms ease-out' }}
                >
                  <Stack gap={0} align="center" style={{ opacity: 0.4 }}>
                    <Text fz={24} fw={500} ta="center" c="grape.5" lineClamp={1} maw={560}>
                      {wheelStrip[0]?.name || ' '}
                    </Text>
                    {wheelStrip[0]?.body && (
                      <Text fz={11} ta="center" c="grape.5" lineClamp={1} maw={520}>
                        {wheelStrip[0].body}
                      </Text>
                    )}
                  </Stack>
                  <Stack
                    gap={0}
                    align="center"
                    style={
                      isLocking
                        ? {
                            animation: 'giveawayLockIn 700ms cubic-bezier(0.22, 1, 0.36, 1) both',
                            transformOrigin: 'center',
                          }
                        : undefined
                    }
                  >
                    <Text fz={56} fw={800} ta="center" c="grape.3" lineClamp={1} maw={620}>
                      {wheelStrip[1]?.name || '…'}
                    </Text>
                    {wheelStrip[1]?.body && (
                      <Text
                        fz={14}
                        ta="center"
                        c="grape.3"
                        lineClamp={1}
                        maw={580}
                        style={{ opacity: 0.7 }}
                      >
                        {wheelStrip[1].body}
                      </Text>
                    )}
                  </Stack>
                  <Stack gap={0} align="center" style={{ opacity: 0.4 }}>
                    <Text fz={24} fw={500} ta="center" c="grape.5" lineClamp={1} maw={560}>
                      {wheelStrip[2]?.name || ' '}
                    </Text>
                    {wheelStrip[2]?.body && (
                      <Text fz={11} ta="center" c="grape.5" lineClamp={1} maw={520}>
                        {wheelStrip[2].body}
                      </Text>
                    )}
                  </Stack>
                </Stack>
                <Box
                  pos="absolute"
                  top={0}
                  bottom={0}
                  left={0}
                  w={3}
                  bg="grape.5"
                  style={{ opacity: 0.7 }}
                />
                <Box
                  pos="absolute"
                  top={0}
                  bottom={0}
                  right={0}
                  w={3}
                  bg="grape.5"
                  style={{ opacity: 0.7 }}
                />
              </Box>
              <Group gap={6} justify="center">
                {[0, 1, 2].map((i) => (
                  <Box
                    key={i}
                    w={10}
                    h={10}
                    bg="grape.4"
                    style={{
                      borderRadius: '50%',
                      animation: `giveawayDot 1s ease-in-out ${i * 0.15}s infinite`,
                    }}
                  />
                ))}
              </Group>
            </Stack>
          ) : (
            <Group mih={220} align="center">
              <Text size="lg" c="dimmed">
                Pripravené na losovanie…
              </Text>
            </Group>
          )}

          <Button
            size="lg"
            color="grape"
            onClick={startDraw}
            disabled={isDrawing}
            loading={isDrawing}
          >
            {isDrawing ? 'Losujem…' : winner ? 'Žrebovať ďalšieho' : 'Spustiť losovanie'}
          </Button>

          {winners.length > 1 && (
            <Stack align="stretch" gap={4} mt="md" w="100%">
              <Group justify="space-between" align="center">
                <Text size="xs" c={winner && !isDrawing ? 'dark.9' : 'dimmed'} fw={600}>
                  Výhercovia ({winners.length})
                </Text>
                <Tooltip label="Skopírovať zoznam">
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color={winner && !isDrawing ? 'dark' : 'gray'}
                    onClick={copyWinners}
                    aria-label="Skopírovať zoznam výhercov"
                  >
                    <IconCopy size={14} />
                  </ActionIcon>
                </Tooltip>
              </Group>
              <Textarea
                readOnly
                value={winnersExportText}
                autosize
                minRows={3}
                maxRows={6}
                styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
              />
            </Stack>
          )}
        </Stack>
      </Modal>
    </>
  );
};
